import Potrace from "potrace";
import DOMPurify from "dompurify";

/**
 * Sanitize an SVG string, stripping scripts, event handlers, foreignObject,
 * javascript: URIs, and other XSS vectors while preserving visual content.
 */
export function sanitizeSvg(svgString) {
  const removed = [];
  DOMPurify.addHook("uponSanitizeElement", (node, data) => {
    if (data.allowedTags && data.allowedTags[data.tagName] === undefined) return;
    if (node.nodeName && ["script", "foreignobject"].includes(node.nodeName.toLowerCase())) {
      removed.push(`<${node.nodeName}>`);
    }
  });
  DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName && /^on/i.test(data.attrName)) {
      removed.push(`${data.attrName}="${data.attrValue?.slice(0, 60)}"`);
    }
    if (data.attrValue && /^\s*javascript:/i.test(data.attrValue)) {
      removed.push(`${data.attrName}="javascript:..."`);
    }
  });

  const clean = DOMPurify.sanitize(svgString, {
    USE_PROFILES: { svg: true, svgFilters: true },
    ADD_TAGS: ["use"],
    FORBID_TAGS: ["script", "foreignObject"],
    FORBID_ATTR: ["onload", "onerror", "onclick", "onmouseover", "onfocus", "onblur"],
  });

  DOMPurify.removeAllHooks();

  if (removed.length > 0) {
    console.warn("[SVG Sanitizer] Stripped dangerous content:", removed);
  } else {
    console.log("[SVG Sanitizer] SVG is clean — nothing stripped");
  }

  return clean;
}

/**
 * Elements that the SvgTrace editor cannot render/edit natively. If any of
 * these are present in an imported SVG, we must re-trace instead of direct-load.
 */
const UNSUPPORTED_EDITOR_ELEMENTS = [
  "image",
  "text",
  "tspan",
  "textPath",
  "foreignObject",
  "use",
  "symbol",
  "filter",
  "mask",
  "clipPath",
  "pattern",
  "linearGradient",
  "radialGradient",
  "marker",
  "switch",
];

/**
 * Detect whether an incoming SVG can be loaded directly into the editor without
 * going through the raster-and-retrace pipeline.
 *
 * Fast paths:
 *   - "marker"     → stamped with data-svgtrace="1" (previously exported by us)
 *   - "structural" → only contains <svg>/<g>/<path> with solid-color fills
 *
 * Anything with gradients, images, text, filters, masks, etc. returns
 * { compatible: false }.
 *
 * @param {string} svgString
 * @returns {{ compatible: boolean, reason: string }}
 */
export function isSvgTraceCompatible(svgString) {
  if (!svgString || typeof svgString !== "string") {
    return { compatible: false, reason: "empty" };
  }

  let svgEl;
  try {
    const doc = new DOMParser().parseFromString(svgString, "image/svg+xml");
    // parseerror becomes an element in HTML namespace; detect it
    if (doc.querySelector("parsererror")) {
      return { compatible: false, reason: "parse-error" };
    }
    svgEl = doc.querySelector("svg");
  } catch {
    return { compatible: false, reason: "parse-error" };
  }
  if (!svgEl) return { compatible: false, reason: "no-svg-root" };

  // Fast path: stamped SvgTrace export
  if (svgEl.getAttribute("data-svgtrace") === "1") {
    return { compatible: true, reason: "marker" };
  }

  // Structural check: no unsupported elements
  for (const tag of UNSUPPORTED_EDITOR_ELEMENTS) {
    if (svgEl.querySelector(tag)) {
      return { compatible: false, reason: `has-${tag}` };
    }
  }

  // The editor requires a flat structure: <svg> children are <path> layers
  // (optionally wrapped in a single top-level <g>). Nested groups, transforms,
  // or non-path child elements break the editor's data model.
  const directChildren = Array.from(svgEl.children).filter(
    (c) => !["defs", "metadata", "title", "desc", "style"].includes(c.tagName.toLowerCase())
          && !c.tagName.includes(":")
  );

  // Allow one optional wrapper <g> (some exporters wrap everything in a single group)
  let layerParent = svgEl;
  if (
    directChildren.length === 1 &&
    directChildren[0].tagName.toLowerCase() === "g" &&
    !directChildren[0].getAttribute("transform")
  ) {
    layerParent = directChildren[0];
  }

  // Every direct child of the layer parent must be a <path> with a `d` attribute
  const layerChildren = Array.from(layerParent.children).filter(
    (c) => !["defs", "metadata", "title", "desc", "style"].includes(c.tagName.toLowerCase())
          && !c.tagName.includes(":")
  );

  if (layerChildren.length === 0) {
    return { compatible: false, reason: "no-paths" };
  }

  for (const child of layerChildren) {
    if (child.tagName.toLowerCase() !== "path") {
      return { compatible: false, reason: "nested-structure" };
    }
    if (!child.getAttribute("d")) {
      return { compatible: false, reason: "path-missing-d" };
    }
    if (child.getAttribute("transform")) {
      return { compatible: false, reason: "has-transform" };
    }
  }

  // All paths must have solid-color fills (no url(...) references, no strokes
  // that reference gradients/patterns). Stroke-only paths with no fill are not
  // editor-compatible either since the editor works on filled regions.
  const paths = svgEl.querySelectorAll("path");
  if (paths.length === 0) {
    return { compatible: false, reason: "no-paths" };
  }

  for (const p of paths) {
    const fill = (p.getAttribute("fill") || "").trim();
    const styleFill = (p.getAttribute("style") || "").match(/fill\s*:\s*([^;]+)/);
    const effectiveFill = (styleFill ? styleFill[1].trim() : fill).toLowerCase();

    if (effectiveFill.startsWith("url(")) {
      return { compatible: false, reason: "gradient-fill" };
    }
    // Missing fill defaults to black in SVG spec — that's fine
    if (effectiveFill && effectiveFill !== "none") {
      // Must be a resolvable color string
      const hex = colorToHex(effectiveFill);
      if (!hex) return { compatible: false, reason: "unresolvable-fill" };
    }

    const stroke = (p.getAttribute("stroke") || "").trim().toLowerCase();
    if (stroke.startsWith("url(")) {
      return { compatible: false, reason: "gradient-stroke" };
    }
  }

  return { compatible: true, reason: "structural" };
}

/**
 * Pick a safe max render size based on device capabilities — mirrors the
 * caps in /Users/.../Converter/components/processImage.js evaluateHardwareCapabilities.
 * Going too high crashes iOS WKWebView and mobile Safari.
 */
function getMaxRenderSize() {
  const isCapacitor = !!(window.Capacitor?.isNativePlatform?.());
  const isIPad =
    /iPad/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
  const memory = navigator.deviceMemory || 4;
  const cpuCores = navigator.hardwareConcurrency || 4;

  if (isCapacitor || isIPad) return 2000; // iOS WKWebView is memory-constrained
  if (isMobile) return 2800;
  if (memory >= 8 && cpuCores >= 8) return 4800; // high-end desktop
  if (memory <= 2 || cpuCores <= 2) return 2800;
  return 4000; // medium desktop default
}

function extractColorsFromSvg(svgElement) {
  const colors = new Set();
  const allElements = svgElement.querySelectorAll("*");

  for (const el of allElements) {
    const fill = getComputedFill(el);
    if (fill && fill !== "none" && fill !== "transparent") {
      const hex = colorToHex(fill);
      if (hex && hex !== "#ffffff") colors.add(hex);
    }

    const stroke = window.getComputedStyle(el).stroke;
    if (stroke && stroke !== "none" && stroke !== "transparent") {
      const hex = colorToHex(stroke);
      if (hex && hex !== "#ffffff") colors.add(hex);
    }
  }

  if (colors.size === 0) colors.add("#000000");
  return Array.from(colors);
}

function getComputedFill(el) {
  const attr = el.getAttribute("fill");
  if (attr) return attr;
  const style = el.getAttribute("style");
  if (style) {
    const match = style.match(/fill\s*:\s*([^;]+)/);
    if (match) return match[1].trim();
  }
  return window.getComputedStyle(el).fill;
}

function colorToHex(color) {
  if (!color) return null;
  color = color.trim();

  if (/^#[0-9a-fA-F]{6}$/.test(color)) return color.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(color)) {
    const r = color[1], g = color[2], b = color[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (match) {
    const hex = (n) => parseInt(n).toString(16).padStart(2, "0");
    return `#${hex(match[1])}${hex(match[2])}${hex(match[3])}`;
  }

  // Use a canvas to resolve named colors
  const ctx = document.createElement("canvas").getContext("2d");
  ctx.fillStyle = color;
  const resolved = ctx.fillStyle;
  if (/^#[0-9a-fA-F]{6}$/.test(resolved)) return resolved.toLowerCase();
  return null;
}

function hexToRgb(hex) {
  hex = hex.replace("#", "");
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

// sRGB -> Lab for perceptual color distance (same approach as processImage.js)
function rgb2lab(r, g, b) {
  const srgb = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  const [R, G, B] = srgb;
  let X = (0.4124564 * R + 0.3575761 * G + 0.1804375 * B) / 0.95047;
  let Y = 0.2126729 * R + 0.7151522 * G + 0.0721750 * B;
  let Z = (0.0193339 * R + 0.1191920 * G + 0.9503041 * B) / 1.08883;
  const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const fx = f(X), fy = f(Y), fz = f(Z);
  return { L: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
}

function deltaE(l1, l2) {
  const dL = l1.L - l2.L, da = l1.a - l2.a, db = l1.b - l2.b;
  return Math.hypot(dL, da, db);
}

function rasterizeSvg(svgString, width, height) {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

/**
 * Quantize every non-background pixel to its closest palette color (in Lab space).
 * Returns a Uint8Array of palette indices (255 = background/transparent).
 *
 * Background rule: a pixel is background if it is transparent OR near-white AND
 * white is not already in the palette. This prevents white SVG backgrounds from
 * bleeding into the nearest palette color.
 */
function quantizePixels(sourceCanvas, colors) {
  const { width, height } = sourceCanvas;
  const ctx = sourceCanvas.getContext("2d");
  const imageData = ctx.getImageData(0, 0, width, height);
  const pixels = imageData.data;

  const paletteRgb = colors.map(hexToRgb);
  const paletteLab = paletteRgb.map((c) => rgb2lab(c.r, c.g, c.b));
  const whiteInPalette = paletteRgb.some(
    (c) => c.r > 245 && c.g > 245 && c.b > 245,
  );

  const BG = 255;
  const assignments = new Uint8Array(width * height);

  for (let p = 0, i = 0; p < width * height; p++, i += 4) {
    const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2], a = pixels[i + 3];

    if (a < 25) {
      assignments[p] = BG;
      continue;
    }

    if (!whiteInPalette && r > 245 && g > 245 && b > 245) {
      assignments[p] = BG;
      continue;
    }

    const pixelLab = rgb2lab(r, g, b);
    let best = 0;
    let bestD = deltaE(pixelLab, paletteLab[0]);
    for (let k = 1; k < paletteLab.length; k++) {
      const d = deltaE(pixelLab, paletteLab[k]);
      if (d < bestD) {
        best = k;
        bestD = d;
      }
    }
    assignments[p] = best;
  }

  return { assignments, width, height, BG };
}

function assignmentsToBinaryMask(assignments, width, height, targetIdx) {
  const bin = new Uint8Array(width * height);
  for (let p = 0; p < assignments.length; p++) {
    bin[p] = assignments[p] === targetIdx ? 255 : 0;
  }
  return bin;
}

function dilate3x3(bin, w, h) {
  const out = new Uint8Array(bin.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let on = 0;
      for (let j = -1; j <= 1 && !on; j++) {
        for (let i = -1; i <= 1 && !on; i++) {
          const xx = x + i, yy = y + j;
          if (xx >= 0 && yy >= 0 && xx < w && yy < h && bin[yy * w + xx]) on = 1;
        }
      }
      out[y * w + x] = on ? 255 : 0;
    }
  }
  return out;
}

function erode3x3(bin, w, h) {
  const out = new Uint8Array(bin.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let all = 1;
      for (let j = -1; j <= 1 && all; j++) {
        for (let i = -1; i <= 1 && all; i++) {
          const xx = x + i, yy = y + j;
          if (xx < 0 || yy < 0 || xx >= w || yy >= h || bin[yy * w + xx] === 0) all = 0;
        }
      }
      out[y * w + x] = all ? 255 : 0;
    }
  }
  return out;
}

function binaryMaskToDataUrl(bin, width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  const data = ctx.createImageData(width, height);
  const px = data.data;
  for (let p = 0, i = 0; p < bin.length; p++, i += 4) {
    const ink = bin[p] > 0;
    px[i] = px[i + 1] = px[i + 2] = ink ? 0 : 255;
    px[i + 3] = 255;
  }
  ctx.putImageData(data, 0, 0);
  return canvas.toDataURL("image/png");
}

function traceWithPotrace(dataUrl, color) {
  return new Promise((resolve, reject) => {
    const trace = new Potrace.Potrace();
    trace.setParameters({
      color,
      turdSize: 2,
      optTolerance: 0.2,
      alphaMax: 1.0,
      threshold: 128,
    });
    trace.loadImage(dataUrl, (err) => {
      if (err) return reject(err);
      resolve(trace.getPathTag());
    });
  });
}

/**
 * Import an arbitrary SVG file by:
 * 1. Rasterizing it to a high-res PNG via <canvas>
 * 2. Extracting the unique fill colors from the SVG DOM
 * 3. Quantizing every pixel to its closest palette color (Lab ΔE)
 * 4. Building a binary mask per color, applying closing + extra dilation so
 *    adjacent layers overlap slightly (no gaps at color boundaries)
 * 5. Tracing each mask with Potrace
 * 6. Assembling a clean paths-only SVG
 *
 * @param {string} svgString - Raw SVG markup
 * @param {function} onProgress - Optional (stage, detail) callback
 * @returns {Promise<{svg: string, colors: string[], width: number, height: number}>}
 */
export async function importSvgAsTraced(svgString, onProgress) {
  const report = (stage, detail) => onProgress && onProgress(stage, detail);

  report("parsing", "Analyzing SVG...");

  svgString = sanitizeSvg(svgString);

  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, "image/svg+xml");
  const svgEl = doc.querySelector("svg");
  if (!svgEl) throw new Error("Invalid SVG file");

  const viewBox = svgEl.getAttribute("viewBox");
  let origW, origH;
  if (viewBox) {
    const parts = viewBox.split(/[\s,]+/).map(Number);
    origW = parts[2];
    origH = parts[3];
  } else {
    origW = parseFloat(svgEl.getAttribute("width")) || 512;
    origH = parseFloat(svgEl.getAttribute("height")) || 512;
  }

  const container = document.createElement("div");
  container.style.cssText = "position:absolute;left:-9999px;top:-9999px;visibility:hidden";
  container.appendChild(doc.documentElement.cloneNode(true));
  document.body.appendChild(container);

  let colors;
  try {
    colors = extractColorsFromSvg(container.querySelector("svg"));
  } finally {
    document.body.removeChild(container);
  }

  if (colors.length === 0) colors = ["#000000"];

  const maxSize = getMaxRenderSize();
  const aspect = origW / origH;
  const renderW = aspect >= 1 ? maxSize : Math.round(maxSize * aspect);
  const renderH = aspect >= 1 ? Math.round(maxSize / aspect) : maxSize;

  report(
    "rasterizing",
    `Rendering at ${renderW}×${renderH} (${colors.length} colors detected)...`,
  );

  const canvas = await rasterizeSvg(svgString, renderW, renderH);

  report("quantizing", "Classifying pixels by color...");

  const { assignments, width, height } = quantizePixels(canvas, colors);

  report("tracing", `Tracing ${colors.length} color layers...`);

  const pathTags = [];
  for (let i = 0; i < colors.length; i++) {
    report("tracing", `Tracing layer ${i + 1}/${colors.length} (${colors[i]})...`);

    let bin = assignmentsToBinaryMask(assignments, width, height, i);
    // Closing: dilate then erode to close sub-pixel gaps inside the layer
    bin = erode3x3(dilate3x3(bin, width, height), width, height);
    // Extra dilation so this layer overlaps adjacent layers by 1px — prevents
    // hairline cracks between traced layers even if the tracer curves diverge.
    bin = dilate3x3(bin, width, height);

    const maskDataUrl = binaryMaskToDataUrl(bin, width, height);
    const pathTag = await traceWithPotrace(maskDataUrl, colors[i]);
    pathTags.push(pathTag);
  }

  report("assembling", "Building final SVG...");

  const svgHead = `<svg xmlns="http://www.w3.org/2000/svg" width="${renderW}" height="${renderH}" viewBox="0 0 ${renderW} ${renderH}" version="1.1">`;
  const svgTail = "</svg>";

  // Add fill-rule="evenodd" to every path (Potrace uses even-odd winding)
  const withFillRule = (tag) => tag.replace(/<path /g, '<path fill-rule="evenodd" ');

  // Draw darker/black layers last (on top) so the 1px overlap from lighter
  // layers is covered, and black outlines stay crisp.
  const isBlack = (c) => c === "#000000";
  let lightPaths = "";
  let blackPath = "";
  for (let i = 0; i < colors.length; i++) {
    if (isBlack(colors[i])) {
      blackPath += withFillRule(pathTags[i]);
    } else {
      lightPaths += withFillRule(pathTags[i]);
    }
  }
  const allPaths = lightPaths + blackPath;

  const outputSvg = svgHead + allPaths + svgTail;

  report("done", "Import complete!");

  return {
    svg: outputSvg,
    colors,
    width: renderW,
    height: renderH,
  };
}
